import React from 'react';
import { ExternalLink } from 'lucide-react';
import { useOrgCrm } from '../../../hooks/rollout/useOrgCrm';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';

interface CrmSourceIndicatorProps {
  crmSynced?: boolean;
  crmID?: string;
  crmSyncReason?: string | null;
}

// Set by remi-sync when a CRM user removes the sync tag from a bound person.
// The contact stays linked (crmID kept) and resumes syncing once re-tagged.
const CRM_TAG_REMOVED_REASON = 'crm_tag_removed';

const CRM_META: Record<string, {
  label: string;
  shortLabel: string;
  icon?: string;
  buildUrl: (subdomain: string, crmId: string) => string;
}> = {
  fub: {
    label: 'Follow Up Boss',
    shortLabel: 'FUB',
    icon: '/fub-icon.png',
    buildUrl: (subdomain, crmId) => `https://${subdomain}.followupboss.com/2/people/view/${crmId}`,
  },
  cinc: {
    label: 'CINC',
    shortLabel: 'CINC',
    buildUrl: (subdomain, crmId) => `https://${subdomain}/dashboard#leads/details/${crmId}/`,
  },
  brivity: {
    label: 'Brivity',
    shortLabel: 'Brivity',
    buildUrl: (subdomain, crmId) => `https://${subdomain}/contacts/${crmId}`,
  },
};

export const CrmSourceIndicator: React.FC<CrmSourceIndicatorProps> = ({
  crmSynced,
  crmID,
  crmSyncReason,
}) => {
  const { crm } = useOrgCrm();
  const organization = useOrganizationStore((s) => s.organization);

  if (!crm) return null;
  const meta = CRM_META[crm.key];
  if (!meta) return null;

  if (!crmSynced && crmSyncReason === CRM_TAG_REMOVED_REASON) {
    return (
      <div
        className="h-5 sm:h-6 inline-flex w-fit items-center gap-1 text-[11px] font-medium leading-none px-2 sm:px-2.5 rounded-full bg-muted text-muted-foreground"
        title={`Not syncing: the sync tag was removed in ${meta.label}. Re-add it there to resume.`}
      >
        <span>
          Not syncing
          <span className="hidden md:inline"> — sync tag removed in {meta.shortLabel}</span>
        </span>
      </div>
    );
  }

  if (!crmSynced) return null;

  // Resolve subdomain from org rollout config
  const subdomain = crm.key === 'fub'
    ? (organization?.fubSetup?.subdomain || organization?.rollout?.fub?.subdomain)
    : organization?.rollout?.[crm.key]?.subdomain;

  const url = subdomain && crmID ? meta.buildUrl(subdomain, crmID) : null;

  // Flatten to a single element (anchor or div) carrying the pill
  // classes directly. Previously the badge was a `<div>` wrapped in an
  // `<a>`, and the anchor's inherited line-height pushed the visible
  // pill below the SummarizeButton's centerline — even though both
  // pills had identical h-5/h-6 classes, the nesting introduced
  // baseline drift inside a `flex items-center` row.
  const PILL_CLASS =
    'h-5 sm:h-6 inline-flex w-fit items-center gap-1 text-[11px] font-medium leading-none px-2 sm:px-2.5 rounded-full fub-badge shadow-sm transition-colors';

  const inner = (
    <>
      {meta.icon && <img src={meta.icon} alt={meta.shortLabel} className="w-3 h-3" />}
      <span>
        <span className="hidden md:inline">Synced with </span>
        {meta.shortLabel}
      </span>
      {url && <ExternalLink className="w-2.5 h-2.5 opacity-70" />}
    </>
  );

  if (url) {
    return (
      <a
        href={url}
        target="_blank"
        rel="noopener noreferrer"
        className={`${PILL_CLASS} no-link-style hover:opacity-80 transition-opacity`}
        title={`Open in ${meta.label}`}
        onClick={(e) => e.stopPropagation()}
      >
        {inner}
      </a>
    );
  }

  return (
    <div className={PILL_CLASS} title={meta.label}>
      {inner}
    </div>
  );
};
